import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIdButton, MobileSortControl } from '@/components/shared';
import { useMediaQuery } from '@/lib/use-media-query';
import { formatProductLine, formatRelativeTimeFromNow, formatShortId } from '../format';
import type { BackofficeProjectRow, ProjectSortBy, SortDirection } from '../types';
import { ProjectDeletedChip, ProjectStatusChip } from './ProjectStatusChip';

const SORTABLE_HEADERS: Record<ProjectSortBy, string> = {
  title: 'Title',
  status: 'Status',
  updatedAt: 'Last updated',
  createdAt: 'Created',
};

const MOBILE_SORT_OPTIONS = [
  { value: 'title', label: SORTABLE_HEADERS.title },
  { value: 'status', label: SORTABLE_HEADERS.status },
  { value: 'updatedAt', label: SORTABLE_HEADERS.updatedAt },
  { value: 'createdAt', label: SORTABLE_HEADERS.createdAt },
] satisfies Array<{ value: ProjectSortBy; label: string }>;

export interface ProjectsTableProps {
  projects: BackofficeProjectRow[];
  sortBy: ProjectSortBy;
  sortDirection: SortDirection;
  showOrganizationColumn?: boolean;
  showOwnerColumn?: boolean;
  onSortChange: (sortBy: ProjectSortBy) => void;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

function SortButton({
  label,
  field,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  label: string;
  field: ProjectSortBy;
  sortBy: ProjectSortBy;
  sortDirection: SortDirection;
  onSortChange: (sortBy: ProjectSortBy) => void;
}) {
  const active = field === sortBy;
  const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      className="focus-ring inline-flex items-center gap-1 rounded-md text-left font-semibold text-neutral-600"
      aria-label={`Sort by ${label}`}
      onClick={() => onSortChange(field)}
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export function ProjectsTable({
  projects,
  sortBy,
  sortDirection,
  showOrganizationColumn = true,
  showOwnerColumn = true,
  onSortChange,
}: ProjectsTableProps) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div>
        <MobileSortControl
          label="Sort studies"
          options={MOBILE_SORT_OPTIONS}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
        <div className="divide-y divide-neutral-100">
          {projects.map((project) => {
            const detailPath = `/projects/${project.id}`;

            return (
              <article
                key={project.id}
                role="button"
                tabIndex={0}
                aria-label={`Open ${project.title}`}
                className="cursor-pointer px-4 py-4 hover:bg-backoffice-50 focus:bg-backoffice-50 focus:outline-none"
                onClick={() => navigate(detailPath)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(detailPath);
                  }
                }}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <h2 className="min-w-0 flex-1 text-body font-semibold text-neutral-900">
                    {project.title}
                  </h2>
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <ProjectStatusChip status={project.status} />
                    {project.deletedAt ? <ProjectDeletedChip /> : null}
                  </div>
                </div>
                <div className="mt-2 text-small text-neutral-600">
                  <div>Product line: {formatProductLine(project.productLine)}</div>
                  {project.interviewCount}{' '}
                  {project.interviewCount === 1 ? 'interview' : 'interviews'}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className={[
          showOrganizationColumn && showOwnerColumn
            ? 'min-w-[1420px]'
            : showOrganizationColumn || showOwnerColumn
              ? 'min-w-[1220px]'
              : 'min-w-[1020px]',
          'w-full border-collapse text-left',
        ].join(' ')}
      >
        <thead className="border-b border-neutral-200 bg-white text-small text-neutral-500">
          <tr>
            <th scope="col" className="w-12 px-4 py-3">
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" className="min-w-[130px] px-4 py-3 font-semibold text-neutral-600">
              ID
            </th>
            <th scope="col" className="min-w-[240px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.title}
                field="title"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            {showOrganizationColumn && (
              <th scope="col" className="min-w-[200px] px-4 py-3 font-semibold text-neutral-600">
                Organization
              </th>
            )}
            <th scope="col" className="min-w-[180px] px-4 py-3 font-semibold text-neutral-600">
              Product line
            </th>
            {showOwnerColumn && (
              <th scope="col" className="min-w-[200px] px-4 py-3 font-semibold text-neutral-600">
                Owner
              </th>
            )}
            <th scope="col" className="min-w-[150px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.status}
                field="status"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[150px] px-4 py-3 font-semibold text-neutral-600">
              Number interviews
            </th>
            <th scope="col" className="min-w-[180px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.updatedAt}
                field="updatedAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="min-w-[160px] px-4 py-3">
              <SortButton
                label={SORTABLE_HEADERS.createdAt}
                field="createdAt"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            </th>
            <th scope="col" className="w-14 px-4 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 text-body">
          {projects.map((project) => {
            const ownerDisplayName = project.ownerName ?? project.ownerEmail ?? 'Unassigned';
            const detailPath = `/projects/${project.id}`;
            const lastUpdated = formatRelativeTimeFromNow(project.updatedAt);

            return (
              <tr
                key={project.id}
                className="cursor-pointer hover:bg-backoffice-50 focus-within:bg-backoffice-50"
                tabIndex={0}
                aria-label={`Open ${project.title}`}
                onClick={() => navigate(detailPath)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(detailPath);
                  }
                }}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    aria-label={`Select ${project.title}`}
                    className="h-4 w-4 rounded border-neutral-300 text-lavender-600"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  />
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium tabular-nums text-neutral-700">
                      {formatShortId(project.id)}
                    </span>
                    <CopyIdButton value={project.id} label="Copy study ID" />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="group inline-flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{project.title}</span>
                    <CopyIdButton value={project.title} label="Copy title" />
                  </div>
                </td>
                {showOrganizationColumn && (
                  <td className="px-4 py-4 text-neutral-700">
                    <div className="group inline-flex items-center gap-2">
                      <Link
                        to={`/organizations/${project.organizationId}`}
                        className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        {project.organizationName}
                      </Link>
                      <CopyIdButton
                        value={project.organizationName}
                        label="Copy organisation name"
                      />
                    </div>
                  </td>
                )}
                <td className="px-4 py-4 text-neutral-700">
                  {formatProductLine(project.productLine)}
                </td>
                {showOwnerColumn && (
                  <td className="px-4 py-4 text-neutral-700">
                    <div className="group inline-flex items-center gap-2">
                      {project.ownerUserId ? (
                        <Link
                          to={`/users/${project.ownerUserId}`}
                          className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        >
                          {ownerDisplayName}
                        </Link>
                      ) : (
                        <span>{ownerDisplayName}</span>
                      )}
                      <CopyIdButton value={ownerDisplayName} label="Copy owner name" />
                    </div>
                  </td>
                )}
                <td className="px-4 py-4">
                  <div className="flex flex-wrap gap-2">
                    <ProjectStatusChip status={project.status} />
                    {project.deletedAt ? <ProjectDeletedChip /> : null}
                  </div>
                </td>
                <td className="px-4 py-4 tabular-nums text-neutral-700">
                  <Link
                    to={`/projects/${project.id}?tab=interviews`}
                    className="focus-ring rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
                    aria-label={`View interviews for ${project.title}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {project.interviewCount}
                  </Link>
                </td>
                <td className="px-4 py-4 text-neutral-700">
                  <span title={lastUpdated.suffix ? formatDate(project.updatedAt) : undefined}>
                    {[lastUpdated.value, lastUpdated.suffix].filter(Boolean).join(' ')}
                  </span>
                </td>
                <td className="px-4 py-4 text-neutral-700">{formatDate(project.createdAt)}</td>
                <td className="px-4 py-4">
                  <button
                    type="button"
                    className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-backoffice-50"
                    aria-label={`Actions for ${project.title}`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
